import type { KeyboardEvent } from "react";
import { useImmer } from "use-immer";

import { atlasSuggestionOptionId } from "./atlasSuggestionIds";

interface AtlasSuggestionsState {
  focused: boolean;
  dismissed: boolean;
  activeIndex: number;
}

const CLOSED: AtlasSuggestionsState = {
  activeIndex: -1,
  dismissed: false,
  focused: false,
};

/**
 * Where an arrow key moves the highlight. A closed panel opens onto its first
 * row (or its last, arrowing up); an open one steps and wraps around.
 */
const nextActiveIndex = ({
  activeIndex,
  forward,
  isOpen,
  total,
}: {
  activeIndex: number;
  forward: boolean;
  isOpen: boolean;
  total: number;
}): number => {
  if (!isOpen) {
    return forward ? 0 : total - 1;
  }
  if (forward) {
    return (activeIndex + 1) % total;
  }
  if (activeIndex <= 0) {
    return total - 1;
  }
  return activeIndex - 1;
};

interface UseAtlasSuggestionsParams {
  suggestions: readonly string[];
  prompt: string;
  onPromptChange: (prompt: string) => void;
}

/**
 * Open/filter/keyboard state for the composer's suggestion dropdown.
 *
 * The panel is open while the input holds focus and at least one suggestion
 * still matches what has been typed, which is what makes the two halves of the
 * design's rule — "typing filters" and "typing dismisses" — one behavior: a
 * query that narrows the list to nothing closes the panel. Escape dismisses
 * explicitly and stays dismissed until the input is focused again, so it does
 * not spring back on the next keystroke.
 */
export const useAtlasSuggestions = ({
  suggestions,
  prompt,
  onPromptChange,
}: UseAtlasSuggestionsParams) => {
  const [state, setState] = useImmer<AtlasSuggestionsState>(CLOSED);

  const query = prompt.trim().toLocaleLowerCase();
  const matches =
    query.length === 0
      ? suggestions
      : suggestions.filter((suggestion) =>
          suggestion.toLocaleLowerCase().includes(query)
        );
  // No suggestions supplied means no dropdown at all — the composer then keeps
  // a plain textbox rather than announcing an empty combobox.
  const isEnabled = suggestions.length > 0;
  const isOpen =
    isEnabled && state.focused && !state.dismissed && matches.length > 0;
  // Filtering can shrink the list under the highlight, so the index is only
  // ever trusted against the matches this render actually shows.
  const activeIndex =
    state.activeIndex < matches.length ? state.activeIndex : -1;

  const commit = (suggestion: string) => {
    onPromptChange(suggestion);
    setState((draft) => {
      draft.dismissed = true;
      draft.activeIndex = -1;
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      if (!isOpen) {
        return;
      }
      event.preventDefault();
      // The dropdown is the innermost thing Escape can close. Inside a dialog
      // the key would otherwise carry on to the modal and close it too.
      event.stopPropagation();
      setState((draft) => {
        draft.dismissed = true;
        draft.activeIndex = -1;
      });
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (matches.length === 0) {
        return;
      }
      event.preventDefault();
      const forward = event.key === "ArrowDown";
      setState((draft) => {
        draft.dismissed = false;
        draft.activeIndex = nextActiveIndex({
          activeIndex,
          forward,
          isOpen,
          total: matches.length,
        });
      });
      return;
    }

    if (event.key === "Enter" && isOpen && activeIndex >= 0) {
      event.preventDefault();
      commit(matches[activeIndex]);
    }
  };

  return {
    activeIndex,
    commit,
    isEnabled,
    isOpen,
    matches,
    activeOptionId:
      isOpen && activeIndex >= 0
        ? atlasSuggestionOptionId(activeIndex)
        : undefined,
    highlight: (index: number) =>
      setState((draft) => {
        draft.activeIndex = index;
      }),
    handleFocus: () =>
      setState((draft) => {
        draft.focused = true;
        draft.dismissed = false;
      }),
    handleBlur: () => setState(CLOSED),
    handleKeyDown,
  };
};
